import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import type React from 'react';
import { cloneElement, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import { useSearchBar } from '~/hooks/useSearchBar';

import { SearchDropdownContent } from './components/SearchDropdownContent';

interface SearchBarProps
  extends Omit<React.ComponentPropsWithRef<'div'>, 'children'> {
  trigger?: React.ReactElement<React.ComponentPropsWithRef<'div'>>;
  inverse?: boolean;
  fixedWidth?: number;
}

export const SearchBar = observer<SearchBarProps>(
  ({ trigger, inverse = false, fixedWidth, className, ...props }) => {
    const { t } = useTranslation();
    const {
      inputRef,
      portalInputRef,
      setSearchWrapperRef,
      setDropdownWrapperRef,
      queryString,
      isLoading,
      recommendationsData,
      isDropdownVisible,
      filteredSearchResults,
      handleStartSearch,
      handleContainerClick,
      handleKeyUp,
      handleInputChange,
      handleCategoryClick,
      handleSelectRecommendation,
      handleHistoryReset,
      handleCloseDropdown,
      handleExploreCatalog,
    } = useSearchBar();

    const [searchBarElement, setSearchBarElement] =
      useState<HTMLDivElement | null>(null);

    const handleRef = useCallback(
      (node: HTMLDivElement | null) => {
        setSearchWrapperRef(node);
        setSearchBarElement(node);
      },
      [setSearchWrapperRef],
    );

    const rect = searchBarElement?.getBoundingClientRect();

    const loadingIconClasses = cn(
      'h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-t-transparent',
      inverse ? 'border-icon-inverse-primary' : 'border-icon-primary',
    );

    const searchIconClasses = cn(
      'shrink-0 cursor-pointer',
      inverse ? 'text-icon-inverse-primary' : 'text-icon-primary',
    );

    const searchBarBaseClasses = cn([
      'relative z-1003 flex h-12 w-full min-w-[400px] max-w-[584px] cursor-text flex-row items-center justify-start gap-2 px-4 outline-none border-transparent',
      'rounded-full border-2 transition-all duration-200',
      inverse
        ? 'bg-surface-inverse-transparent-2 hover:border-bg-base hover:bg-bg-inverse-base'
        : company.match({
            dubaiexpresscom:
              'rounded-[8px] bg-surface-transparent-2 hover:bg-surface-transparent-3',
            default:
              'bg-surface-transparent-2 hover:border-bg-inverse-base hover:bg-bg-base',
          }),
      company.isMarketplaceVariant &&
        'rounded-[16px] hover:bg-(--layer-inverse-surface-transparent-3)',
    ]);

    const getSearchInputClasses = (placeholderHidden: boolean) =>
      cn([
        'h-full flex-1 border-none bg-transparent outline-none',
        '[font:var(--text-300-regular)]',
        inverse
          ? 'text-text-inverse-primary placeholder:text-text-inverse-tertiary'
          : 'text-text-primary placeholder:text-text-tertiary',
        placeholderHidden && 'placeholder:opacity-0',
      ]);

    const linearBlackBg =
      '[background:linear-gradient(180deg,var(--surface-transparent-3)_0%,var(--surface-transparent-5)_100%)]';

    if (typeof document === 'undefined') {
      return null;
    }

    return (
      <>
        {document &&
          createPortal(
            <div
              className={cn(
                isDropdownVisible
                  ? 'pointer-events-auto'
                  : 'pointer-events-none opacity-0',
                'fixed inset-0 z-1000',
                '[-webkit-backdrop-filter:blur(9px)] [backdrop-filter:blur(9px)]',
                '[transition:var(--dropdown-opacity-transition)]',
                company.match({
                  mcosmetic: linearBlackBg,
                  dubaiexpresscom: linearBlackBg,
                  default:
                    '[background:linear-gradient(180deg,rgba(0,0,0,0)_0%,var(--layer-surface-transparent-7)_100%)]',
                }),
              )}
              onClick={handleCloseDropdown}
            />,
            document.body,
          )}

        <div
          {...props}
          data-active={isDropdownVisible ? 'active' : ''}
          className={cn(
            trigger
              ? 'cursor-pointer'
              : cn(
                  'relative z-1002 flex flex-[1_1_0] flex-row items-center justify-center px-[6px]',
                  inverse ? 'bg-bg-inverse-base' : 'bg-bg-base',
                ),
            className,
          )}
        >
          {trigger ? (
            cloneElement(trigger, {
              ref: handleRef,
              tabIndex: 0,
              onClick: handleContainerClick,
            })
          ) : (
            <div
              className={cn([
                searchBarBaseClasses,
                isDropdownVisible &&
                  (inverse
                    ? 'border-bg-base bg-bg-inverse-base'
                    : company.match({
                        dubaiexpresscom:
                          'border-stroke-primary bg-surface-transparent-3',
                        default: 'border-bg-inverse-base bg-bg-base',
                      })),
              ])}
              ref={handleRef}
              tabIndex={0}
              onClick={handleContainerClick}
            >
              {isLoading ? (
                <div className={loadingIconClasses} />
              ) : (
                <Icon
                  onClick={handleStartSearch}
                  type="search-sm"
                  size={16}
                  className={searchIconClasses}
                />
              )}

              <input
                ref={inputRef}
                data-input="search"
                type="text"
                value={queryString}
                className={getSearchInputClasses(isDropdownVisible)}
                placeholder={t('header:searchPlaceholder')}
                onKeyUp={handleKeyUp}
                onChange={handleInputChange}
              />
            </div>
          )}
        </div>

        {rect &&
          document &&
          createPortal(
            <div
              ref={setDropdownWrapperRef}
              className={cn(
                'fixed z-1003 rounded-[32px] p-2',
                inverse ? 'bg-bg-inverse-base' : 'bg-bg-base',
                trigger ? 'right-0 m-auto' : 'right-auto -m-2',
                isDropdownVisible ? 'block' : 'hidden',
              )}
              style={{
                top: `${trigger ? 16 : rect.top}px`,
                left: `${trigger ? 0 : rect.left}px`,
                width:
                  fixedWidth ||
                  (trigger ? 'fit-content' : `${rect.width + 16}px`),
              }}
            >
              <div
                className={cn([
                  searchBarBaseClasses,
                  inverse
                    ? 'border-bg-base bg-bg-inverse-base'
                    : company.match({
                        dubaiexpresscom:
                          'border-stroke-primary bg-surface-transparent-3',
                        default: 'border-bg-inverse-base bg-bg-base',
                      }),
                ])}
              >
                {isLoading ? (
                  <div className={loadingIconClasses} />
                ) : (
                  <Icon
                    onClick={handleStartSearch}
                    type="search-sm"
                    size={16}
                    className={searchIconClasses}
                  />
                )}

                <input
                  ref={portalInputRef}
                  data-input="search"
                  type="text"
                  value={queryString}
                  className={getSearchInputClasses(true)}
                  placeholder={t('header:searchPlaceholder')}
                  onKeyUp={handleKeyUp}
                  onChange={handleInputChange}
                  autoFocus
                />
              </div>

              <div
                className={cn([
                  'z-1001 w-full cursor-default overflow-y-auto px-2 pt-6 pb-2',
                  'max-h-[calc(100vh-100px)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                  '[transition:var(--dropdown-opacity-transition),var(--dropdown-transform-transition)]',
                  'pointer-events-auto translate-y-0 scale-y-100 opacity-100',
                ])}
              >
                <SearchDropdownContent
                  recommendationsData={recommendationsData}
                  handleHistoryReset={handleHistoryReset}
                  handleSelectRecommendation={handleSelectRecommendation}
                  handleCategoryClick={handleCategoryClick}
                  handleCloseDropdown={handleCloseDropdown}
                  filteredSearchResults={filteredSearchResults}
                  queryString={queryString}
                  handleStartSearch={handleStartSearch}
                  handleExploreCatalog={handleExploreCatalog}
                  inverse={inverse}
                />
              </div>
            </div>,
            document.body,
          )}
      </>
    );
  },
);
